import { useMemo } from 'react';
import { selectLastHistoricalSpeaker } from 'features/layout/features/order/orderSlice';
import {
  selectIsActiveSpeaker,
  selectLocalAudioBroadcasting,
  selectRemoteBroadcastingState,
} from 'features/streaming/streamingSlice';
import { useAppSelector } from 'store/hooks';
import { selectPageStreams } from 'features/layout/features/pagination/paginationSlice';
import { selectMaximizedStream } from 'features/layout/features/content/contentSlice';
import { selectLocalUser } from 'features/users/usersSlice';

export const useActiveSpeakerState = () => {
  const lastHistoricalSpeaker = useAppSelector(selectLastHistoricalSpeaker);
  const localUser = useAppSelector(selectLocalUser);

  const remoteAudioEnabled = useAppSelector((state) =>
    lastHistoricalSpeaker
      ? selectRemoteBroadcastingState(state, lastHistoricalSpeaker.id, 'audio')
      : false
  );

  const localAudioEnabled = useAppSelector(selectLocalAudioBroadcasting);

  const activeSpeaker = useMemo(() => {
    if (lastHistoricalSpeaker && remoteAudioEnabled) {
      return lastHistoricalSpeaker;
    }

    return localUser;
  }, [lastHistoricalSpeaker, remoteAudioEnabled, localUser]);

  const active = useAppSelector((state) =>
    activeSpeaker ? selectIsActiveSpeaker(state, activeSpeaker.id) : false
  );

  const streams = useAppSelector(selectPageStreams);
  const maximizedStream = useAppSelector(selectMaximizedStream);

  const activeSpeakerStream = streams.find((stream) => stream?.userId === activeSpeaker?.id);

  const audioEnabled = activeSpeaker?.local ? localAudioEnabled : remoteAudioEnabled;

  const isActiveSpeakerMaximized = maximizedStream?.userId === activeSpeaker?.id;

  const hasNoStreams = !activeSpeakerStream && !audioEnabled && !isActiveSpeakerMaximized;

  return {
    speaker: activeSpeaker,
    active,
    audioEnabled,
    hasNoStreams,
  };
};
